import { useState } from "react";
import { Alert, BasicTable, Button, DropdownSelect, Icon, Modal } from "@/components/atoms";
import { useAppSelector } from "@/redux/hooks";
import { configSelector } from "@/redux/slices/config/selectors";
import { clsx } from "@/utils";
import { useBulkBooking } from "../BulkBookingContext";
import { BOOKING_FIELDS, unmappedRequiredFields } from "../bookingFields";

/** Placeholder for the "no column chosen" option, so a mapping can be cleared from the dropdown. */
const UNMAPPED = "Select column";

/**
 * Step 3 — "Match Columns". Left column is our booking fields, right is a dropdown of the file's
 * headers. Auto-mapping does the work for a file built from our template; this step exists for
 * everything else.
 */
export const MatchColumnsStep = () => {
  const { headers, mapping, setMapping, resetMapping } = useBulkBooking();
  const [confirmReset, setConfirmReset] = useState(false);

  // The white-label name of whoever's site this is, so the left column reads "Acme Cars Booking
  // Field" against "Your Table Header" and it's obvious which side is which. Same selector and
  // trailing-period strip the booking sheet's PaymentPanel uses.
  const config = useAppSelector(configSelector);
  const operatorName = (config?.name ?? "").replace(/\.+$/, "").trim();

  const missing = unmappedRequiredFields(mapping);

  // A header can only feed one field, so the options exclude columns already claimed elsewhere.
  const optionsFor = (fieldKey: string) => {
    const claimed = new Set(
      Object.entries(mapping)
        .filter(([key, index]) => key !== fieldKey && index !== null)
        .map(([, index]) => index)
    );
    const available = headers
      .map((header, index) => ({ header, index }))
      .filter(({ header, index }) => header && (!claimed.has(index) || mapping[fieldKey as never] === index));

    return [UNMAPPED, ...available.map(({ header }) => header)];
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Which fields are still unmapped, by name. A disabled Next button says something's wrong but
          not what, and with twelve rows in a scrolling table the empty dropdown is easy to miss.
          Warning rather than danger: nothing is broken, the step just isn't finished. */}
      {missing.length > 0 && (
        <Alert
          type="warning"
          message={`Match ${missing.length === 1 ? "one more field" : `${missing.length} more fields`} to continue: ${missing
            .map((field) => field.label)
            .join(", ")}.`}
        />
      )}

      <Button variant="tertiary" size="sm" className="self-end" onClick={() => setConfirmReset(true)}>
        Reset Mappings
      </Button>

      <BasicTable className="max-h-[45vh]">
        <BasicTable.Row>
          <BasicTable.HeaderCell className="sticky top-0 z-10">
            {operatorName ? `${operatorName} Booking Field` : "Booking Field"}
          </BasicTable.HeaderCell>
          <BasicTable.HeaderCell className="sticky top-0 z-10">Your Table Header</BasicTable.HeaderCell>
          <BasicTable.HeaderCell className="sticky top-0 z-10 w-16" />
        </BasicTable.Row>
        <>
          {BOOKING_FIELDS.map((field) => {
            const mappedIndex = mapping[field.key];
            const mappedHeader = mappedIndex === null ? undefined : headers[mappedIndex];

            return (
              <BasicTable.Row key={field.key} className={clsx(field.required && mappedIndex === null && "bg-warning-light/40")}>
                <BasicTable.Cell>
                  {field.label}
                  {!field.required && <span className="ml-1 text-neutral-silver-gray">(optional)</span>}
                </BasicTable.Cell>
                <BasicTable.Cell>
                  <DropdownSelect
                    size="md"
                    options={optionsFor(field.key)}
                    value={mappedHeader ?? UNMAPPED}
                    placeholder={UNMAPPED}
                    onChange={(header: string) =>
                      setMapping(field.key, header === UNMAPPED ? null : headers.findIndex((candidate) => candidate === header))
                    }
                  />
                </BasicTable.Cell>
                <BasicTable.Cell className="w-16">
                  {mappedIndex !== null && <Icon name="TickCircle" variant="Bold" size="md" className="text-success" />}
                </BasicTable.Cell>
              </BasicTable.Row>
            );
          })}
        </>
      </BasicTable>

      <Modal
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        variant="web-small"
        // 400px on sm+, matching ConfirmCardModal — the other compact confirmation. Mobile stays
        // fluid so it can't overflow a small screen.
        className="sm:w-[400px]"
        title="Clear all field mappings?"
        description="This will remove all the column-to-field matches you've made so far. You'll need to reassign each column again before continuing."
        // flex-1 splits the row evenly. Modal's button container already supplies the 12px gap, and
        // its gap-8 the 32px above — neither is set here.
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="lg" className="flex-1" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button
              variant="custom"
              size="lg"
              className="flex-1 bg-danger text-white hover:bg-danger/90"
              onClick={() => {
                resetMapping();
                setConfirmReset(false);
              }}
            >
              Yes, Reset
            </Button>
          </>
        )}
      />
    </div>
  );
};
